const DARK_STYLE_ID = "rio-assist-dark-mode";
const THEME_ATTRIBUTE = "data-theme";
const THEME_DARK = "dark";
const SHADOW_ROOT_MAX_ATTEMPTS = 50;

const surface = "#1f2226";
const surfaceRaised = "#2a2e33";
const surfaceSunken = "#181a1d";
const border = "#3a4047";
const text = "#e4e8eb";
const textMuted = "#9aa5ae";

const dark = (selectors: string) =>
  selectors
    .split(",")
    .map((selector) => `:host([${THEME_ATTRIBUTE}='${THEME_DARK}']) ${selector.trim()}`)
    .join(", ");

const darkModeCss = `
  :host([${THEME_ATTRIBUTE}='${THEME_DARK}']) {
    color: ${text};
  }

  ${dark(".panel, .dialog, .fullscreen-chat, .fullscreen-header, .conversation-menu")} {
    background: ${surface} !important;
    color: ${text} !important;
  }

  ${dark(".panel-header, .fullscreen-header")} {
    border-color: ${border} !important;
  }

  ${dark(".fullscreen-shell")} {
    background: ${surfaceSunken} !important;
  }

  ${dark(".fullscreen-grid")} {
    background: linear-gradient(180deg, ${surfaceSunken} 0%, ${surface} 100%) !important;
  }

  ${dark(".fullscreen-header__tab")} {
    border-bottom-color: ${text} !important;
  }

  ${dark(`.panel-title, .hero-card, .hero-card h3, .dialog__message, .consultant-agent__intro,
    .consultant-prompt__text, .consultant-follow-up__text, .short-answer-toggle, .voice-recording-label,
    .voice-transcript, .voice-input-locked, .attachment-card__meta strong, .recent-conversations-button,
    .conversation-item, .conversation-menu button, .fullscreen-header__brand-toggle, .fullscreen-header__tab,
    form textarea, form input`)} {
    color: ${text} !important;
  }

  ${dark(`.message time, .footnote, .suggestions-label, .attachment-card__meta span, .conversation-item__time,
    .conversation-loading, .voice-transcript--unavailable, .message__content blockquote`)} {
    color: ${textMuted} !important;
  }

  ${dark(".message")} {
    background: ${surfaceRaised} !important;
    border-color: ${border} !important;
    color: ${text} !important;
  }

  ${dark(".message--user")} {
    background: #33404a !important;
    border-color: #415260 !important;
  }

  ${dark(".message__content code")} {
    background: ${surfaceSunken} !important;
  }

  ${dark(".message__content blockquote")} {
    border-left-color: ${border} !important;
  }

  ${dark(".suggestion, .attachment-card, .attachment-thumb, form, .dialog__button--ghost, .dialog__input")} {
    background: ${surfaceRaised} !important;
    border-color: ${border} !important;
    color: ${text} !important;
  }

  ${dark(".suggestion")} {
    color: ${textMuted} !important;
  }

  ${dark("form input::placeholder, form textarea::placeholder")} {
    color: ${textMuted} !important;
  }

  ${dark(".short-answer-toggle__track")} {
    background: ${border} !important;
  }

  ${dark(".short-answer-toggle__track--on")} {
    background: var(--accent-color, #30b4c0) !important;
  }

  ${dark(".error-banner")} {
    background: #3a2422 !important;
    color: #f0a39b !important;
  }

  ${dark(".conversation-error")} {
    color: #f0a39b !important;
  }

  ${dark(".conversations-panel--sidebar")} {
    background: ${surfaceSunken} !important;
    border-right-color: ${border} !important;
  }

  ${dark(".conversations-panel__surface")} {
    background: ${surfaceRaised} !important;
  }

  ${dark(".conversation-search, .conversations-panel--sidebar .conversation-search")} {
    background: ${surfaceRaised} !important;
    border-color: ${border} !important;
  }

  ${dark(".conversation-search input")} {
    color: ${text} !important;
  }

  ${dark(".conversation-item:hover, .conversation-item:focus-visible")} {
    background: rgba(48, 180, 192, 0.12) !important;
  }

  ${dark(".new-conversation-cta__button")} {
    background: transparent !important;
  }

  ${dark(".close-button img")} {
    filter: brightness(1.8);
  }
`;

const injectDarkModeStyles = (shadowRoot: ShadowRoot) => {
  if (shadowRoot.querySelector(`#${DARK_STYLE_ID}`)) return;
  const style = document.createElement("style");
  style.id = DARK_STYLE_ID;
  style.textContent = darkModeCss;
  shadowRoot.appendChild(style);
};

const syncTheme = (host: HTMLElement) => {
  const html = document.documentElement;
  const apply = () => {
    if (html.getAttribute(THEME_ATTRIBUTE) === THEME_DARK) {
      host.setAttribute(THEME_ATTRIBUTE, THEME_DARK);
    } else {
      host.removeAttribute(THEME_ATTRIBUTE);
    }
  };
  apply();
  const observer = new MutationObserver(apply);
  observer.observe(html, { attributes: true, attributeFilter: [THEME_ATTRIBUTE] });
  return () => observer.disconnect();
};

export const applyRioAssistDarkMode = (host: HTMLElement): (() => void) => {
  const stopThemeSync = syncTheme(host);
  let shadowObserver: MutationObserver | null = null;
  let frame = 0;
  let attempts = 0;

  const attach = () => {
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) {
      attempts += 1;
      if (attempts < SHADOW_ROOT_MAX_ATTEMPTS) {
        frame = requestAnimationFrame(attach);
      }
      return;
    }
    injectDarkModeStyles(shadowRoot);
    shadowObserver = new MutationObserver(() => injectDarkModeStyles(shadowRoot));
    shadowObserver.observe(shadowRoot, { childList: true });
  };

  attach();

  return () => {
    cancelAnimationFrame(frame);
    shadowObserver?.disconnect();
    stopThemeSync();
  };
};
